/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { Fragment, useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import AppsIcon from '@atlaskit/icon/core/apps';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoSettingsModalSurface } from '@atlassian/rovo-settings-modal/common/utils/rovo-settings-config';
import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';

import { type ProductKeys3P, is3pProductKey } from '../../../../constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../../constants/schemas/3p-config';

import { i18n } from './messages';
import { STATIC_FALLBACK_ORDER } from './registry';
import type {
	CONNECT_APPS_BG_DURATION,
	CONNECT_APPS_SLIDE_DURATION,
	FADE_DURATION,
} from './timing';
import type { IconEntry } from './types';
import { isElevated, isTextOut, useConnectAppsAnimation } from './use-connect-apps-animation';
import { useConnectAppsCooldown } from './use-connect-apps-cooldown';

// --- Icon sequence --------------------------------------------------------

const MAX_APP_ICONS = 5;

const STATIC_SEQUENCE: readonly IconEntry[] = [
	{ type: 'ads' },
	...STATIC_FALLBACK_ORDER.map<IconEntry>((slug) => ({ type: 'img', slug })),
	{ type: 'ads' },
];

/**
 * Rank icons by smartlink usage from any 3P app that has a config (icon).
 * Backfill with the static fallback order, deduping.
 */
const useIconSequence = (
	smartLinkUsage: Record<string, number> | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined,
): readonly IconEntry[] => {
	return useMemo(() => {
		if (!smartLinkUsage || !thirdPartyConfigs) {
			return STATIC_SEQUENCE;
		}

		const rankedByUsage = Object.entries(smartLinkUsage)
			.filter(([key]) => is3pProductKey(key) && thirdPartyConfigs[key as ProductKeys3P]?.icon)
			.sort((a, b) => b[1] - a[1])
			.map(([key]) => key as ProductKeys3P);

		const seen = new Set(rankedByUsage);
		const backfill = STATIC_FALLBACK_ORDER.filter(
			(key) => !seen.has(key) && thirdPartyConfigs[key]?.icon,
		);
		const combined = [...rankedByUsage, ...backfill].slice(0, MAX_APP_ICONS);

		return [
			{ type: 'ads' } as IconEntry,
			...combined.map<IconEntry>((slug) => ({ type: 'img', slug })),
			{ type: 'ads' } as IconEntry,
		];
	}, [smartLinkUsage, thirdPartyConfigs]);
};

// --- Layout / styling constants -------------------------------------------

const PILL_GAP_PX = 6;
const PILL_HORIZONTAL_PADDING_PX = 8;
const LABEL_MAX_WIDTH_PX = 160;

// CSS strings cannot interpolate imported values (compiled `no-imported-style-values`),
// so we redeclare the durations here and assert they match `./timing` at compile time.
const BG_DURATION_MS = 200;
const SLIDE_DURATION_MS = 350;
const FADE_DURATION_MS = 200;
const EASE_PILL = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const EASE_ICON_CROSSFADE = 'cubic-bezier(0.45, 0, 0.55, 1)';

// Compile-time guard: drift between the timing module and the CSS values would
// silently desync animations and tests. This fails to typecheck if either side
// changes without the other.
const _cssTimingMatchesTimingModule: readonly [
	typeof CONNECT_APPS_BG_DURATION,
	typeof CONNECT_APPS_SLIDE_DURATION,
	typeof FADE_DURATION,
] = [BG_DURATION_MS, SLIDE_DURATION_MS, FADE_DURATION_MS];
void _cssTimingMatchesTimingModule;

// --- Styles ---------------------------------------------------------------

const pillButtonStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	paddingBlockStart: 0,
	paddingBlockEnd: 0,
	paddingInlineEnd: 0,
	borderRadius: token('radius.medium', '6px'),
	borderStyle: 'none',
	cursor: 'pointer',
	font: 'inherit',
	transition: `background-color ${BG_DURATION_MS}ms ease, padding ${BG_DURATION_MS}ms ease, gap ${SLIDE_DURATION_MS}ms ${EASE_PILL}`,
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025', '2px'),
	},
});

const labelWrapperStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	userSelect: 'none',
	pointerEvents: 'none',
	transition: `max-width ${SLIDE_DURATION_MS}ms ${EASE_PILL}, opacity ${SLIDE_DURATION_MS}ms ease`,
});

const iconStackStyles = css({
	width: token('space.300', '24px'),
	height: token('space.300', '24px'),
	position: 'relative',
	flexShrink: 0,
	pointerEvents: 'none',
});

const iconLayerStyles = css({
	position: 'absolute',
	inset: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: `opacity ${FADE_DURATION_MS}ms ${EASE_ICON_CROSSFADE}`,
});

const appsIconLayerStyles = css({
	position: 'absolute',
	inset: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: `opacity ${FADE_DURATION_MS}ms ease`,
});

const appIconWrapperStyles = css({
	width: '24px',
	height: '24px',
	boxSizing: 'border-box',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	borderRadius: token('radius.medium', '6px'),
	overflow: 'hidden',
	border: `${token('border.width')} solid ${token('color.background.neutral.bold')}`,
});

const appIconInnerStyles = css({
	display: 'inline-flex',
	// 22px inner area (24px wrapper - 1px border on each side) / 24px icon.
	transform: 'scale(0.9167)',
});

// --- Subcomponents --------------------------------------------------------

type IconStackProps = {
	sequence: readonly IconEntry[];
	activeIndex: number;
	elevated: boolean;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
};

const IconStack = ({ sequence, activeIndex, elevated, thirdPartyConfigs }: IconStackProps) => (
	<span css={iconStackStyles}>
		{sequence.map((entry, i) => {
			const isActive = i === activeIndex;
			const key = entry.type === 'ads' ? `ads-${i}` : entry.slug;
			const Icon = entry.type === 'img' ? thirdPartyConfigs?.[entry.slug]?.icon : null;
			return (
				<span key={key} css={iconLayerStyles} style={{ opacity: isActive ? 1 : 0 }}>
					{Icon ? (
						<span css={appIconWrapperStyles}>
							<span css={appIconInnerStyles}>
								<Icon size="small" />
							</span>
						</span>
					) : (
						<AdsIconCrossfade elevated={elevated} />
					)}
				</span>
			);
		})}
	</span>
);

/**
 * Crossfades between the inverse-color and subtle variants of the Apps icon
 * based on whether the pill is elevated. Both labels are empty because the
 * stack is decorative — accessibility comes from the button's `aria-label`.
 */
const AdsIconCrossfade = ({ elevated }: { elevated: boolean }) => (
	<Fragment>
		<span css={appsIconLayerStyles} style={{ opacity: elevated ? 1 : 0 }}>
			<AppsIcon label="" color={token('color.icon.inverse')} />
		</span>
		<span css={appsIconLayerStyles} style={{ opacity: elevated ? 0 : 1 }}>
			<AppsIcon label="" color={token('color.icon.subtle')} />
		</span>
	</Fragment>
);

type PillButtonProps = {
	spanRef?: React.Ref<HTMLSpanElement>;
	ariaLabel: string;
	label: string;
	sequence: readonly IconEntry[];
	activeIndex: number;
	elevated: boolean;
	textOut: boolean;
	onClick: () => void;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
};

/**
 * Rendered as a `<span role="button">` instead of a native `<button>` because
 * this component can be placed inside an element that is already a `<button>`
 * (e.g. Jira's `Pressable` skeleton wrapper).  Nesting `<button>` elements
 * produces invalid HTML that causes browsers to mis-parse the SSR payload,
 * breaking hydration and duplicating the entire page.
 *
 * See: https://jplat.atlassian.net/browse/JRB-1245
 */
const PillButton = ({
	spanRef,
	ariaLabel,
	label,
	sequence,
	activeIndex,
	elevated,
	textOut,
	onClick,
	thirdPartyConfigs,
}: PillButtonProps) => (
	<span
		ref={spanRef}
		role="button"
		tabIndex={0}
		onClick={(e) => {
			e.stopPropagation();
			onClick();
		}}
		onKeyDown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				e.stopPropagation();
				onClick();
			}
		}}
		aria-label={ariaLabel}
		data-testid="quick-find-connect-apps-pill"
		css={pillButtonStyles}
		style={{
			gap: textOut ? PILL_GAP_PX : 0,
			paddingLeft: elevated ? PILL_HORIZONTAL_PADDING_PX : 0,
			background: elevated ? token('color.background.neutral.bold') : 'transparent',
		}}
	>
		<span
			aria-hidden="true"
			css={labelWrapperStyles}
			style={{
				maxWidth: textOut ? LABEL_MAX_WIDTH_PX : 0,
				opacity: textOut ? 1 : 0,
			}}
		>
			<Text size="small" weight="medium" color="color.text.inverse">
				{label}
			</Text>
		</span>
		<IconStack
			sequence={sequence}
			activeIndex={activeIndex}
			elevated={elevated}
			thirdPartyConfigs={thirdPartyConfigs}
		/>
	</span>
);

// --- Public component -----------------------------------------------------

export type ConnectAppsSearchIconProps = {
	start?: boolean;
	onAppsClick?: () => void;
	spanRef?: React.Ref<HTMLSpanElement>;
	cloudId?: string;
	primaryProduct?: string;
	smartLinkUsage?: Record<string, number>;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
};

export const ConnectAppsSearchIcon = ({
	start = false,
	onAppsClick,
	spanRef,
	cloudId,
	primaryProduct,
	smartLinkUsage,
	thirdPartyConfigs,
}: ConnectAppsSearchIconProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const sequence = useIconSequence(smartLinkUsage, thirdPartyConfigs);

	const { canAnimate, recordAnimation, dismissPermanently } = useConnectAppsCooldown();

	const { phase, activeIndex, exitEarly } = useConnectAppsAnimation({
		start,
		sequenceLength: sequence.length,
		canStart: canAnimate,
		onStart: recordAnimation,
	});

	const elevated = isElevated(phase);
	const textOut = isTextOut(phase);

	const renderPill = (openModal?: () => void) => (
		<PillButton
			spanRef={spanRef}
			ariaLabel={formatMessage(i18n.connectAppsButtonAriaLabel)}
			label={formatMessage(i18n.connectAppsLabel)}
			sequence={sequence}
			activeIndex={activeIndex}
			elevated={elevated}
			textOut={textOut}
			thirdPartyConfigs={thirdPartyConfigs}
			onClick={() => {
				onAppsClick?.();
				openModal?.();
				dismissPermanently();
				if (textOut) {
					exitEarly();
				}
			}}
		/>
	);

	if (cloudId && primaryProduct) {
		return (
			<RovoSettingsModal
				product={primaryProduct}
				cloudId={cloudId}
				surface={RovoSettingsModalSurface.SEARCH_QF_PILL}
			>
				{({ open }) => renderPill(() => open({ defaultTab: 'discovery' }))}
			</RovoSettingsModal>
		);
	}

	return renderPill();
};
